import { apiClient } from "../client";

/**
 * Live-class endpoints. Student-facing reads verified against the Postman
 * collection's "LMS — Live Classes (Student)" folder; `list`, `create`,
 * `get`, `start` and `complete` come from the Instructor/Admin folder and
 * power the dashboard's instructor screens plus the education app's host flow;
 * `remove` (DELETE /live-classes/:id) powers the dashboard's delete action;
 * `update` (PUT /live-classes/:id) powers the reschedule action. The rest of
 * the Instructor/Admin CRUD (sync-recording) is intentionally not wrapped here.
 */
export interface LiveClass {
  id: number;
  title: string;
  description?: string | null;
  course_id?: number | null;
  instructor_id?: number;
  instructor?: { id?: number; first_name?: string; last_name?: string; [key: string]: unknown } | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: "scheduled" | "live" | "completed" | "cancelled" | string | null;
  is_recorded?: boolean | number;
  recording_url?: string | null;
  meeting_id?: string | null;
  /** Which video service the class runs on. null until its room first opens
   * (host start or first join) — at that point the admin's current General
   * Settings choice is locked onto the class and never changes again. */
  provider?: LiveStreamingProvider | null;
  [key: string]: unknown;
}

/** The two video providers the backend can run a live class on. The admin
 * picks the default (General Settings → Live Streaming); every class is
 * stamped with one when its room opens. Clients never choose — they read it
 * off the class / the join payload and mount the matching SDK. */
export type LiveStreamingProvider = "videosdk" | "livekit";

/**
 * Parse a live-class `start_time`/`end_time` into a Date. The backend stores
 * the instructor's wall-clock input verbatim (typing "18:30" stores "18:30")
 * but serializes it back with a UTC suffix ("…T18:30:00.000000Z"), so these
 * strings must be read as wall-clock — trusting the fake zone marker shifts
 * every render by the viewer's UTC offset (verified live: a 6:30 PM IST class
 * displayed as next-day 12:00 AM). Also accepts "YYYY-MM-DD HH:mm:ss".
 * Returns null when absent or unparseable.
 */
export function parseLiveClassTime(value?: string | null): Date | null {
  if (!value) return null;
  const wall = String(value)
    .trim()
    .replace(" ", "T")
    .replace(/(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/i, "");
  const d = new Date(wall);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface MyLiveClassesResponse {
  success?: boolean;
  status?: boolean;
  data: {
    upcoming?: LiveClass[];
    recorded?: LiveClass[];
    [key: string]: unknown;
  } | LiveClass[];
  [key: string]: unknown;
}

export interface LiveClassEnvelope {
  success?: boolean;
  status?: boolean;
  data: LiveClass;
}

/** GET /live-classes — instructors see only their own classes, admins see
 * all (per the Postman prose). UNVERIFIED live shape — kept permissive:
 * either a bare array or a Laravel paginator, possibly enveloped. */
export interface LiveClassListResponse {
  success?: boolean;
  status?: boolean;
  data:
    | LiveClass[]
    | { data: LiveClass[]; total?: number; last_page?: number; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * The join credentials for one participant. Always carries the original
 * VideoSDK shape — meeting_id + token (+ user_name, role, live_class) — and,
 * since the provider switch, says which SDK to mount (`provider`) and adds
 * what LiveKit's client needs when that is the one. `token` is the SDK's own
 * JWT either way (VideoSDK auth token / LiveKit access token); `meeting_id`
 * is the VideoSDK meetingId / the LiveKit room name. Host-ness comes from the
 * server (`is_host` / `role`) — the token enforces it, the flag only decides
 * which controls are drawn. Read through resolveLiveSession() in the
 * education app rather than by hand.
 */
export interface JoinLiveClassPayload {
  provider?: LiveStreamingProvider;
  meeting_id?: string;
  token?: string;
  user_name?: string | null;
  role?: "host" | "participant" | string;
  is_host?: boolean;
  can_publish?: boolean;
  /** ISO timestamp — when `token` stops working. */
  expires_at?: string;
  live_class?: LiveClass | null;
  /** VideoSDK only: the participantId pinned inside `token`. */
  participant_id?: string;
  /** LiveKit only: the wss:// URL to connect to. */
  server_url?: string;
  /** LiveKit only: the room name (same value as meeting_id). */
  room_name?: string;
  /** LiveKit only: this participant's identity as pinned inside `token`. */
  identity?: string;
  [key: string]: unknown;
}

export interface JoinLiveClassResponse extends JoinLiveClassPayload {
  success?: boolean;
  status?: boolean;
  data?: JoinLiveClassPayload;
}

export interface LiveClassRecordingResponse {
  success?: boolean;
  status?: boolean;
  recording_url?: string | null;
  data?: { recording_url?: string | null; [key: string]: unknown };
  [key: string]: unknown;
}

/** POST /live-classes (Instructor/Admin). Field names per the Postman
 * collection: title + start_time/end_time are required — NOT
 * "scheduled_at"/"duration". The backend generates the VideoSDK
 * meeting_id server-side. */
export interface CreateLiveClassPayload {
  title: string;
  /** "YYYY-MM-DD HH:mm:ss" — must be now or later. */
  start_time: string;
  /** Must be after start_time. */
  end_time: string;
  description?: string;
  /** Omit for a standalone live class (enrollable on its own). */
  course_id?: number | string;
  /** Admin only — defaults to the authenticated user server-side. */
  instructor_id?: number | string;
  is_recorded?: boolean;
  recording_url?: string;
  [key: string]: unknown;
}

/** PUT /live-classes/:id — every field is optional ("sometimes" rules, per
 * the Postman collection); the same start-now-or-later / end-after-start
 * corrections as create apply when times are sent. The dashboard uses this
 * for rescheduling (new start_time/end_time). */
export interface UpdateLiveClassPayload {
  title?: string;
  description?: string;
  course_id?: number | string;
  /** "YYYY-MM-DD HH:mm:ss" */
  start_time?: string;
  /** Must be after start_time. */
  end_time?: string;
  is_recorded?: boolean;
  recording_url?: string;
  status?: string;
  [key: string]: unknown;
}

export const liveClasses = {
  /** Instructor/Admin console list — instructors see only their own classes. */
  list: (params: { page?: number; per_page?: number } = {}) =>
    apiClient.get<LiveClassListResponse>("/live-classes", { params: { ...params } }),

  /** Schedule a new live class (Instructor/Admin). */
  create: (payload: CreateLiveClassPayload) =>
    apiClient.post<LiveClassEnvelope>("/live-classes", payload),

  /** Update a live class — assigned instructor or Admin only (enforced
   * server-side). Powers the dashboard's reschedule action. */
  update: (liveClassId: number | string, payload: UpdateLiveClassPayload) =>
    apiClient.put<LiveClassEnvelope>(`/live-classes/${liveClassId}`, payload),

  /** Single class by id (Instructor/Admin console read). */
  get: (liveClassId: number | string) =>
    apiClient.get<LiveClassEnvelope>(`/live-classes/${liveClassId}`),

  /** Delete a live class — only the assigned instructor or an Admin may
   * delete (enforced server-side; 403 otherwise). */
  remove: (liveClassId: number | string) =>
    apiClient.delete<{ success?: boolean; status?: boolean; message?: string }>(
      `/live-classes/${liveClassId}`,
    ),

  /** Set status=live and dispatch LiveClassStarted (assigned instructor or
   * Admin only). The host still enters the room via join() afterwards. */
  start: (liveClassId: number | string) =>
    apiClient.post<LiveClassEnvelope>(`/live-classes/${liveClassId}/start`, {}),

  /** POST /live-classes/:id/complete — end the session ("Live class ended
   * successfully."): sets status=completed and returns the updated record
   * (recording_url populated when the class was recorded). Owning instructor
   * or Admin only — others get 403 "You are not allowed to manage this live
   * class." Body is empty. */
  complete: (liveClassId: number | string) =>
    apiClient.post<LiveClassEnvelope>(`/live-classes/${liveClassId}/complete`, {}),

  /** Upcoming + recorded live classes for the authenticated student. */
  mine: () => apiClient.get<MyLiveClassesResponse>("/my-live-classes"),

  /** Only valid for standalone live classes (course_id null) — course-linked
   * ones inherit enrollment from the parent course enrollment. */
  enroll: (liveClassId: number | string) =>
    apiClient.post<LiveClassEnvelope>(`/live-classes/${liveClassId}/enroll`, {}),

  /** Join credentials for whichever provider the class runs on (see
   * JoinLiveClassPayload — `provider` says which). Requires an active
   * enrollment (or being the instructor / an admin). */
  join: (liveClassId: number | string) =>
    apiClient.get<JoinLiveClassResponse>(`/live-classes/${liveClassId}/join`),

  /** Requires enrollment; 422 if not recorded or not yet completed. */
  recording: (liveClassId: number | string) =>
    apiClient.get<LiveClassRecordingResponse>(`/live-classes/${liveClassId}/recording`),

  /** Requires enrollment. */
  room: (liveClassId: number | string) =>
    apiClient.get<unknown>(`/live-classes/${liveClassId}/room`),

  roomRecording: (liveClassId: number | string) =>
    apiClient.get<LiveClassRecordingResponse>(`/live-classes/${liveClassId}/room/recording`),
};
